/* eslint-disable @typescript-eslint/naming-convention */
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import Immutable from 'immutable';
import type { RangeKeyDict, Range } from 'react-date-range';

import { Container, FormikWrapper } from '@uncinc/uncinc-react-kitchen-sink';
import Select from '@uncinc/uncinc-react-kitchen-sink/dist/components/Form/Select';
import { View } from '@uncinc/react-drupal-core';
import type {
  ImmutableEntity,
  ImmutableRootState,
  NodeEntity,
  TaxonomyEntity,
} from '@uncinc/react-drupal-core/types';

import DatePicker from '../../../../../components/DatePicker';
import { getVocabulary } from '../../../../../store/actions/UI';
import type { ActivitiesOverviewProps } from '..';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import './style.scss';

const DRUPAL_DATE_TIME_FORMAT = 'yyyy-MM-dd HH:mm:ss';
const LOCATION_TYPE_ACTIVITY = 206;

type EntityList<Entity> = Immutable.List<ImmutableEntity<Entity>>;
type TermList = EntityList<TaxonomyEntity>;

type Filters = {
  field_target_audience: ActivitiesOverviewProps['targetAudience'];
  field_type_activiteit_target_id?: number;
  field_location_target_id?: number;
  field_date_ranges_value_2?: {
    min: string;
    max: string;
  };
};

type QueryParams = {
  type?: number | null;
  location?: number | null;
  date?: [Date, Date] | null;
  page?: number | null;
};

const useMappedFilterTerms = (vocabulary: string) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const terms = useSelector<ImmutableRootState<TermList>, TermList>(
    (state) => state.getIn(['uiInternal', 'vocabularies', vocabulary], Immutable.List()),
  );

  useEffect(() => {
    if (terms.size === 0) {
      const display = vocabulary === 'locations' ? 'activity_locations' : 'block_1';
      dispatch(getVocabulary(vocabulary, display));
    }
  }, [dispatch, terms, vocabulary]);

  return useMemo(() => {
    const filteredTerms = vocabulary === 'locations' ?
      terms.filter((term) => term.getIn(['field_location_type', 0, 'target_id']) === LOCATION_TYPE_ACTIVITY) :
      terms;

    return Immutable.List([{ value: 0, label: t('general.all') }])
      .merge(filteredTerms.map((term) => ({
        value: term.getIn(['tid', 0, 'value']),
        label: term.getIn(['name', 0, 'value']),
      }))).toArray();
  }, [vocabulary, terms, t]);
};

const useQueryParams = () => {
  const params = new URLSearchParams(window.location.search);
  const dates = params.has('date') ? params.get('date')?.split(',') : null;

  const [queryParams, setQueryParams] = useState<QueryParams>({
    type: params.has('type') ? parseInt(params.get('type') || '0', 10) : null,
    location: params.has('location') ? parseInt(params.get('location') || '0', 10) : null,
    date: dates && dates[0] && dates[1] ? [new Date(`${dates[0]}T00:00:00`), new Date(`${dates[1]}T23:59:59`)] : null,
    page: params.has('page') ? parseInt(params.get('page') || '0', 10) : 0,
  });

  useEffect(() => {
    const newQueryParams = new URLSearchParams(window.location.search);
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== null && value !== 0) {
        if (key === 'date' && value instanceof Array) {
          newQueryParams.set(key, [
            format(value[0], 'yyyy-MM-dd'),
            format(value[1], 'yyyy-MM-dd'),
          ].join(','));
        } else {
          newQueryParams.set(key, String(value));
        }
      } else {
        newQueryParams.delete(key);
      }
    });

    if (newQueryParams.size > 0) {
      window.history.replaceState({}, '', `${window.location.pathname}?${newQueryParams.toString()}`);
    } else {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [queryParams]);

  return { queryParams, setQueryParams };
};

const ActivitiesOverviewRegenboog: React.FC<ActivitiesOverviewProps> = ({
  display,
  targetAudience,
}) => {
  const { t } = useTranslation();
  const { queryParams, setQueryParams } = useQueryParams();

  const currentPage = useMemo(() => queryParams.page || 0, [queryParams.page]);
  const setCurrentPage = useCallback(
    (page: number) => setQueryParams((state) => ({ ...state, page })),
    [setQueryParams],
  );
  const selectionRange = useMemo<[Range]>(() => [
    {
      startDate: queryParams.date?.[0],
      endDate: queryParams.date?.[1],
      key: 'selection',
    },
  ], [queryParams.date]);

  const setSelectionRange = useCallback<(rangesByKey: RangeKeyDict) => void>(({ selection }) => {
    if (selection && selection.startDate && selection.endDate) {
      const { startDate, endDate } = selection;
      setQueryParams((state) => ({ ...state, date: [startDate, endDate], page: 0 }));
    } else {
      setQueryParams((state) => ({ ...state, date: null, page: 0 }));
    }
  }, [setQueryParams]);

  const filters = useMemo(() => {
    const newFilters: Filters = {
      field_target_audience: targetAudience,
    };

    if (queryParams.type) {
      newFilters.field_type_activiteit_target_id = queryParams.type;
    }
    if (queryParams.location) {
      newFilters.field_location_target_id = queryParams.location;
    }
    if (queryParams.date) {
      newFilters.field_date_ranges_value_2 = {
        min: format(queryParams.date[0], DRUPAL_DATE_TIME_FORMAT),
        max: format(queryParams.date[1], DRUPAL_DATE_TIME_FORMAT),
      };
    }

    return newFilters;
  }, [queryParams, targetAudience]);

  const showPinnedActivities = useMemo(
    () => currentPage === 0 && Object.keys(filters).length === 1,
    [currentPage, filters],
  );

  // Get all pinned activities for excluding in main view.
  const pinnedActivities = useSelector<ImmutableRootState<EntityList<NodeEntity>>, string>(
    (state) => (
      state.getIn(['view', 'content_overview', `${display}_pinned`, 'entities'], Immutable.List())
        .map((activity) => activity.getIn(['nid', 0, 'value'])).join('+')
    ),
  );

  const mappedTypeTerms = useMappedFilterTerms('activity_types');
  const mappedLocationTerms = useMappedFilterTerms('locations');

  const onChangeType = useCallback((type: number) => {
    if (type === 0) {
      setQueryParams((state) => ({ ...state, type: null, page: 0 }));
    } else {
      setQueryParams((state) => ({ ...state, type, page: 0 }));
    }
  }, [setQueryParams]);

  const onChangeLocation = useCallback((location: number) => {
    if (location === 0) {
      setQueryParams((state) => ({ ...state, location: null, page: 0 }));
    } else {
      setQueryParams((state) => ({ ...state, location, page: 0 }));
    }
  }, [setQueryParams]);

  const resetFilters = useCallback(() => {
    setQueryParams({
      type: null,
      location: null,
      date: null,
      page: 0,
    });
  }, [setQueryParams]);

  return (
    <Container className="ParagraphContentOverview">
      <FormikWrapper initialValues={{ type: 0 }}>
        <div className="row">
          <div className="col-md-6 col-lg-4">
            <Select
              title={t('activity.what_to_do')}
              fieldName="type"
              placeholder={t('activity.placeholder_activity_type')}
              options={mappedTypeTerms}
              className="Select"
              classNamePrefix="Select"
              defaultValue={queryParams.type}
              onChange={onChangeType}
            />
          </div>
          <div className="col-md-6 col-lg-8">
            <DatePicker
              label={t('activity.when_to_do')}
              selectionRange={selectionRange}
              setSelectionRange={setSelectionRange}
              quickSelectMonths={3}
              placeholder={t('activity.date_picker_placeholder')}
            />
          </div>
          {targetAudience === 'participant' && (
            <div className="col-md-6 col-lg-4">
              <Select
                title={t('vacancy.where_to_do')}
                fieldName="location"
                placeholder={t('vacancy.placeholder_input_location')}
                options={mappedLocationTerms}
                className="Select"
                classNamePrefix="Select"
                defaultValue={queryParams.location}
                onChange={onChangeLocation}
              />
            </div>
          )}
        </div>
      </FormikWrapper>
      {showPinnedActivities && (
        <View
          id="content_overview"
          className="PinnedActivities"
          display={`${display}_pinned`}
          page={currentPage}
          noPagination
          type="activities"
          filters={filters}
        />
      )}

      <View
        id="content_overview"
        display={display}
        page={currentPage}
        setCurrentPage={setCurrentPage}
        type="activities"
        filters={filters}
        resetFilters={resetFilters}
        args={showPinnedActivities && pinnedActivities ? [pinnedActivities] : undefined}
      />

    </Container>
  );
};

export default ActivitiesOverviewRegenboog;
